import { api } from "../lib";

/**
 * Cancer Notification Service - API client for malignancy notification endpoints
 */

export interface MalignancyInfo {
  specimenNumber: string;
  specimenLabel: string;
  diagnosisText: string;
  categoryName: string;
}

export interface NotificationAttempt {
  id: string;
  notificationId: string;
  channel: "voice" | "sms" | "fax";
  recipient: string;
  status: string;
  attemptedAt: string;
  completedAt?: string;
  externalId?: string;
  errorCode?: string;
  errorMessage?: string;
  durationSeconds?: number;
}

export interface PhysicianContact {
  id: string;
  firstName: string;
  lastName: string;
  phone?: string;
  fax?: string;
}

export interface CancerNotification {
  id: string;
  caseId: string;
  physicianId: string;
  diagnosisSummary?: string;
  malignancyType?: string;
  urgencyLevel: string;
  status: string;
  notificationMethod?: string;
  notifiedAt?: string;
  acknowledgedAt?: string;
  acknowledgedBy?: string;
  notes?: string;
  createdAt: string;
  physician?: PhysicianContact;
  attempts?: NotificationAttempt[];
}

export interface CancerNotificationResponse {
  notification: CancerNotification | null;
  malignancies: MalignancyInfo[];
  requiresNotification: boolean;
  hasDocumentedNotification: boolean;
}

export interface InitiateCallResult {
  success: boolean;
  attemptId?: string;
  callSid?: string;
  error?: string;
}

export interface SendSmsResult {
  success: boolean;
  attemptId?: string;
  messageSid?: string;
  error?: string;
}

export interface SendFaxResult {
  success: boolean;
  attemptId?: string;
  faxId?: string;
  error?: string;
}

export interface DocumentOutcomeResult {
  success: boolean;
  notification?: CancerNotification;
  error?: string;
}

/**
 * Get cancer notification status and info for a case
 */
export function getCancerNotification(
  caseId: string
): Promise<CancerNotificationResponse> {
  return api.get<CancerNotificationResponse>(
    `/cases/${caseId}/cancer-notification`
  );
}

/**
 * Create or update cancer notification record
 */
export function createCancerNotification(
  caseId: string,
  data: {
    physicianId: string;
    diagnosisSummary?: string;
    malignancyType?: string;
    urgencyLevel?: "routine" | "urgent" | "critical";
  }
): Promise<CancerNotification> {
  return api.post<CancerNotification>(
    `/cases/${caseId}/cancer-notification`,
    data
  );
}

/**
 * Initiate a phone call to the physician
 */
export function initiateCall(
  caseId: string,
  phoneNumber?: string
): Promise<InitiateCallResult> {
  return api.post<InitiateCallResult>(
    `/cases/${caseId}/cancer-notification/call`,
    { phoneNumber }
  );
}

/**
 * Send SMS to the physician
 */
export function sendSms(
  caseId: string,
  phoneNumber?: string,
  message?: string
): Promise<SendSmsResult> {
  return api.post<SendSmsResult>(`/cases/${caseId}/cancer-notification/sms`, {
    phoneNumber,
    message,
  });
}

/**
 * Send fax to the physician
 */
export function sendFax(
  caseId: string,
  faxNumber?: string
): Promise<SendFaxResult> {
  return api.post<SendFaxResult>(`/cases/${caseId}/cancer-notification/fax`, {
    faxNumber,
  });
}

/**
 * Get all notification attempts for a case
 */
export function getAttempts(caseId: string): Promise<NotificationAttempt[]> {
  return api.get<NotificationAttempt[]>(
    `/cases/${caseId}/cancer-notification/attempts`
  );
}

/**
 * Get recording URL for a call attempt
 */
export function getRecordingUrl(
  caseId: string,
  attemptId: string
): Promise<{ url: string }> {
  return api.get<{ url: string }>(
    `/cases/${caseId}/cancer-notification/recording/${attemptId}`
  );
}

/**
 * Document the notification outcome and complete the notification
 */
export function documentOutcome(
  caseId: string,
  data: {
    outcome:
      | "spoke_with_physician"
      | "spoke_with_staff"
      | "left_voicemail"
      | "sms_delivered"
      | "sms_no_response"
      | "fax_confirmed"
      | "fax_failed"
      | "unable_to_reach";
    spokeWith?: string;
    notes?: string;
    notificationMethod: "phone" | "sms" | "fax";
  }
): Promise<DocumentOutcomeResult> {
  return api.post<DocumentOutcomeResult>(
    `/cases/${caseId}/cancer-notification/document`,
    data
  );
}
